import {
    DeclarationStatusAnalysisTitles,
    DeclarationType,
    EDeclarationsDeclarationQueryParams,
} from "../../models/app/eDeclarations";
import { DeclarationTableFilters } from "../../api/admin/eDeclarations";
import { declarationTableFiltersDefaultValues } from "../../hooks/useDeclarationTableFiltersLocalStorage";
import { match } from "ts-pattern";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import { declarationTableAllowedIdsEn } from "../DriverDeclarationsTable";
import useTableStore from "../../stores/useTableStore";

export const declarationsQueryDefaultValues: EDeclarationsDeclarationQueryParams =
    {
        declarationTypes: null,
        hasExpiredDeclaration: null,
        hasCompletedDeclaration: null,
        hasIncompleteDeclaration: null,
        hasExpiringDeclaration: null,
    };

export const declarationAnalysisActions = (
    declarationTableFiltersStore: DeclarationTableFilters,
    setDeclarationTableFiltersStore: (
        value:
            | DeclarationTableFilters
            | ((val: DeclarationTableFilters) => DeclarationTableFilters)
    ) => void,
    setIsLoadingFilter: (value: boolean) => void
) => {
    const { updateTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.declarations,
        true
    );

    const setScrollToTable = useTableStore((state) => state.setScrollToTable);

    const handleApplyFilter = <AllowedIds extends string>(
        filter: DeclarationTableFilters,
        columns?: AllowedIds[]
    ) => {
        setIsLoadingFilter(true);

        setDeclarationTableFiltersStore(filter);
        if (columns) {
            updateTableConfig(columns);
        }

        setScrollToTable(true);
        setIsLoadingFilter(false);
    };

    // keep company filters on when clicking through
    const defaultValues = {
        ...declarationTableFiltersDefaultValues,
        companyQuery: declarationTableFiltersStore.companyQuery,
    };

    const declarationStatusAnalysisActions = (
        title: DeclarationStatusAnalysisTitles
    ) => {
        match(title)
            .with(DeclarationStatusAnalysisTitles.EDeclarationsComplete, () => {
                const filter = {
                    ...defaultValues,
                    declarationsQuery: {
                        ...declarationsQueryDefaultValues,
                        declarationTypes: [DeclarationType.EDeclaration],
                        hasCompletedDeclaration: true,
                    },
                };
                handleApplyFilter(filter, [
                    declarationTableAllowedIdsEn.status,
                ]);
            })
            .with(
                DeclarationStatusAnalysisTitles.EDeclarationsIncomplete,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [DeclarationType.EDeclaration],
                            hasIncompleteDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.status,
                    ]);
                }
            )
            .with(
                DeclarationStatusAnalysisTitles.EDeclarationsDueToExpire,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [DeclarationType.EDeclaration],
                            hasExpiringDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.declarationExpires,
                    ]);
                }
            )
            .with(DeclarationStatusAnalysisTitles.EDeclarationsExpired, () => {
                const filter = {
                    ...defaultValues,
                    declarationsQuery: {
                        ...declarationsQueryDefaultValues,
                        declarationTypes: [DeclarationType.EDeclaration],
                        hasExpiredDeclaration: true,
                    },
                };
                handleApplyFilter(filter, [
                    declarationTableAllowedIdsEn.declarationExpires,
                ]);
            })
            .with(
                DeclarationStatusAnalysisTitles.PaperDeclarationsComplete,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [
                                DeclarationType.PaperDeclaration,
                            ],
                            hasCompletedDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.status,
                    ]);
                }
            )
            .with(
                DeclarationStatusAnalysisTitles.PaperDeclarationsIncomplete,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [
                                DeclarationType.PaperDeclaration,
                            ],
                            hasIncompleteDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.status,
                    ]);
                }
            )
            .with(
                DeclarationStatusAnalysisTitles.PaperDeclarationsDueToExpire,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [
                                DeclarationType.PaperDeclaration,
                            ],
                            hasExpiringDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.declarationExpires,
                    ]);
                }
            )
            .with(
                DeclarationStatusAnalysisTitles.PaperDeclarationsExpired,
                () => {
                    const filter = {
                        ...defaultValues,
                        declarationsQuery: {
                            ...declarationsQueryDefaultValues,
                            declarationTypes: [
                                DeclarationType.PaperDeclaration,
                            ],
                            hasExpiredDeclaration: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.declarationExpires,
                    ]);
                }
            )
            .with(DeclarationStatusAnalysisTitles.DriversWithUKLicences, () => {
                const filter = {
                    ...defaultValues,
                    licenceOriginQuery: {
                        ukOrigin: true,
                        niOrigin: false,
                        foreignOrigin: false,
                    },
                };
                handleApplyFilter(filter, [
                    declarationTableAllowedIdsEn.licenceOrigin,
                ]);
            })
            .with(DeclarationStatusAnalysisTitles.DriversWithNILicences, () => {
                const filter = {
                    ...defaultValues,
                    licenceOriginQuery: {
                        ukOrigin: false,
                        niOrigin: true,
                        foreignOrigin: false,
                    },
                };
                handleApplyFilter(filter, [
                    declarationTableAllowedIdsEn.licenceOrigin,
                ]);
            })
            .with(
                DeclarationStatusAnalysisTitles.DriversWithForeignLicences,
                () => {
                    const filter = {
                        ...defaultValues,
                        licenceOriginQuery: {
                            ukOrigin: false,
                            niOrigin: false,
                            foreignOrigin: true,
                        },
                    };
                    handleApplyFilter(filter, [
                        declarationTableAllowedIdsEn.licenceOrigin,
                    ]);
                }
            )
            .exhaustive();
    };

    return {
        declarationStatusAnalysisActions,
    };
};
